import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomDistributorSplitView from './CustomDistributorSplitView';

const distributorTrackingLabels = getLabels('Distributor');
const distributorTrackingBase = createTrackingConfig('Distributor');

export const distributorConfig = {
  entityName: 'Distributor',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 11, // Distributor entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/distributor',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      // No lookups for base distributor entity currently
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/distributor'
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality for distributors
    masterEntityTypeId: 11, // Distributor master entity type ID for audit logs
  },

  // Custom Split View Panel configuration for Distributor module
  customSplitViewPanel: {
    enabled: true,
    component: CustomDistributorSplitView  // Use the custom Distributor split view component
  },
  
  // Custom handlers for Distributor-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in distributorPage
    onDelete: null, // Will be defined in distributorPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in distributorPage
    
    // Distributor-specific actions
    onActivate: null, // Will handle distributor activation
    onDeactivate: null, // Will handle distributor deactivation
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      await fetch(`/distributor/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: toGroup === 'active' }),
      });
      // Optionally refresh list afterward
    }
  },

  // Service configuration (reference only for generic service mapping)
  service: {
    name: 'distributorService',
    methods: {
      getAll: 'getAllDistributors',
      getById: 'getDistributorById',
      create: 'createDistributor',
      update: 'updateDistributor',
      delete: 'deleteDistributor',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Distributor Management',
  description: 'Comprehensive distributor management system for distributor relationships and compliance tracking',
  icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'createdBy', 'updatedBy', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'fileAttachments', 'fileAttachmentCount', 'complaints'
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'name',
      label: 'Distributor Name',
      type: 'text',
      required: true,
      placeholder: 'Enter distributor name',
      table: { header: 'Name', width: '220px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
        message: 'Name is required and must be 2-255 characters'
      }
    },
    {
      name: 'code',
      label: 'Distributor Code',
      type: 'text',
      placeholder: 'Enter distributor code (e.g., SAP code)',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text',
      placeholder: 'Enter category (e.g., Wholesaler, Retailer, Online)',
      table: { header: 'Category', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 255
      }
    },
    {
      name: 'contactEmail',
      label: 'Contact Email',
      type: 'text',
      placeholder: 'name@example.com',
      table: { header: 'Email', width: '200px', sortable: true },
      filterable: true,
      validation: {
        pattern: /^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/, // allow empty or valid email
        message: 'Email must be valid if provided'
      }
    },
    {
      name: 'contactPhone',
      label: 'Contact Phone',
      type: 'text',
      placeholder: '+30 210 1234567',
      table: { header: 'Phone', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      placeholder: 'Enter street address',
      table: { header: 'Address', width: '250px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 500
      }
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'Enter city',
      table: { header: 'City', width: '150px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'postalCode',
      label: 'Postal Code',
      type: 'text',
      placeholder: 'Enter postal code',
      table: { header: 'Postal Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 20
      }
    },
    {
      name: 'taxId',
      label: 'Tax ID (Α.Φ.Μ.)',
      type: 'text',
      placeholder: 'Enter tax identification number',
      table: { header: 'Tax ID', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'taxOffice',
      label: 'Tax Office (Δ.Ο.Υ.)',
      type: 'text',
      placeholder: 'Enter tax office',
      table: { header: 'Tax Office', width: '150px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'status',
      label: 'Active',
      type: 'switch',
      required: true,
      defaultValue: true,
      switchLabel: 'Active distributor',
      description: 'Marks whether the distributor is currently active',
      labelPosition: 'left',
      table: {
        header: 'Active',
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: true
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_distributors',

  // Backend field mappings (nested display fields if any)
  fieldMappings: {
    // none required for base distributor fields
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    status: [true, false], // Active distributors first
    name: 'asc' // Alphabetical by default
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'name', 'code', 'category', 'status'
      ]
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'contactEmail', 'contactPhone'
      ]
    },
    {
      name: 'location',
      label: 'Location',
      icon: '📍',
      fields: [
        'address', 'city', 'postalCode'
      ]
    },
    {
      name: 'tax',
      label: 'Tax Information',
      icon: '📋',
      fields: [
        'taxId', 'taxOffice'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'code', 'category'],
      fieldSpans: {
        'name': 'md:col-span-6',
        'code': 'md:col-span-3',
        'category': 'md:col-span-3'
      }
    },
    {
      title: 'Contact Information',
      icon: '☎️',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['contactEmail', 'contactPhone']
    },
    {
      title: 'Address Information',
      icon: '📍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['address', 'city', 'postalCode'],
      fieldSpans: {
        'address': 'md:col-span-12',
        'city': 'md:col-span-6',
        'postalCode': 'md:col-span-6'
      }
    },
    {
      title: 'Tax Information',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['taxId', 'taxOffice']
    },
    {
      title: 'Status',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['status']
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...distributorTrackingBase,
    fields: distributorTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: distributorTrackingLabels.createdDateLabel,
            userEmptyText: distributorTrackingLabels.notSpecifiedText,
            dateEmptyText: distributorTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: distributorTrackingLabels.updatedDateLabel,
              userEmptyText: distributorTrackingLabels.notModifiedText,
              dateEmptyText: distributorTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Distributor labels + custom overrides)
  labels: {
    // Using predefined Distributor labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Distributor',
    plural: 'Distributors',
    pageTitle: 'Distributor Management',
    pageSubtitle: 'Comprehensive distributor management for distributor relationships and compliance',
    searchPlaceholder: 'Search distributors by name, code, category, or contact...',
    viewTitle: 'View Distributor',
    noSelectionText: 'Select a distributor to see details',
    
    // Action labels
    activateDistributor: 'Activate Distributor',
    deactivateDistributor: 'Deactivate Distributor',
    contactDistributor: 'Contact Distributor'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Audit: createdAt/createdById on create only; updatedAt/updatedById on update only (server / user-id header)
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Ensure boolean for status
    if (transformed.status !== undefined) {
      if (transformed.status === '' || transformed.status === null) {
        delete transformed.status;
      } else if (typeof transformed.status === 'string') {
        transformed.status = transformed.status === 'true';
      } else {
        transformed.status = Boolean(transformed.status);
      }
    }

    // Convert date fields (if any)
    const dateFields = [];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading distributors...',
    noData: 'No distributors found. Create your first distributor to get started.',
    loadError: 'Failed to load distributors',
    saveSuccess: 'Distributor saved successfully!',
    saveError: 'Failed to save distributor',
    deleteSuccess: 'Distributor deleted successfully!',
    deleteError: 'Failed to delete distributor',
    deleteConfirm: 'Are you sure you want to delete this distributor? This action cannot be undone and will remove all associated data.',
    activateSuccess: 'Distributor activated successfully!',
    deactivateSuccess: 'Distributor deactivated successfully!'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    name: { short: 'Official name of the distributor company or organization.' },
    code: { short: 'Internal distributor code or SAP code for identification.' },
    category: { short: 'Category or type of distributor (Wholesaler, Retailer, Online, etc.).' },
    contactEmail: { short: 'Primary email address for contacting the distributor.' },
    contactPhone: { short: 'Primary phone number for contacting the distributor.' },
    address: { short: 'Street address of the distributor.' },
    city: { short: 'City where the distributor is located.' },
    postalCode: { short: 'Postal code of the distributor address.' },
    taxId: { short: 'Tax Identification Number (Α.Φ.Μ.) for the distributor.' },
    taxOffice: { short: 'Tax Office (Δ.Ο.Υ.) where the distributor is registered.' },
    status: { short: 'Whether the distributor is currently active and available for business.' },
    fileAttachments: { short: 'Supporting documents, contracts, certificates, or related files for this distributor.' }
  }
};

export default distributorConfig;

